import { normalizeText, normalizeUnit } from './schema';
import { isYieldTrackingProduct } from '../inventoryYield';

const LEGACY_FIELDS = ['recipeComponents', 'ingredients', 'insumos', 'components', 'componentes', 'recipeItems', 'comboItems', 'formula'];
const CONFIG_FIELDS = ['recipe', 'promotion', 'isIceCream', 'base', 'baseInsumoId', 'baseInsumoName', 'extraItems', 'sabores', 'gramosPorSabor', 'servilletas', 'cucharas'];
const hasContent = (value) => value != null && (!Array.isArray(value) || value.length > 0);
const LEGACY_ICE_CREAM_FIELDS = ['icecream', 'iceCream', 'ice_cream', 'helado'];
const LEGACY_ICE_CREAM_FLAG_FIELDS = ['icecreamEnabled', 'isIceCream', 'isIcecream'];
const truthyLegacyFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['true', '1', 'yes', 'y', 'si', 'sí', 'on', 'enabled'].includes(value.trim().toLowerCase());
  return false;
};
const hasFunctionalLegacyIceCreamValue = (value) => {
  if (value && typeof value === 'object') return true;
  if (truthyLegacyFlag(value)) return true;
  if (typeof value === 'string') return /ice|helad|sabor|flavor|variable/.test(value.trim().toLowerCase());
  return false;
};
const normalizeCategoryValue = (value) => typeof value === 'string' ? value.trim() : (typeof value?.value === 'string' ? value.value.trim() : '');

// IDs in this intermediate result are source IDs, NEVER destination IDs.
// The builder remaps every typed reference before validating the portable contract.
export function extractProductDependencies(product, catalog) {
  const errors = [], warnings = [], references = new Set(), selectors = [];
  const fail = (message) => errors.push(message);
  const checkKeys = (value, allowed, path) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) { fail(`${path}: estructura inválida`); return {}; }
    Object.keys(value).forEach((key) => { if (!allowed.includes(key)) fail(`${path}.${key}: configuración no soportada`); });
    return value;
  };
  const reference = (id, path) => {
    if (typeof id !== 'string' || !id.trim() || id !== id.trim()) { fail(`${path}: falta un ID válido`); return ''; }
    references.add(id);
    return id;
  };
  const numeric = (value, fallback) => value === undefined ? fallback : (typeof value === 'number' || (typeof value === 'string' && value.trim()) ? Number(value) : NaN);
  const items = (value, path) => {
    if (value === undefined) return [];
    if (!Array.isArray(value)) { fail(`${path}: debe ser una lista`); return []; }
    return value.map((raw, index) => {
      const p = `${path}[${index}]`;
      const entry = checkKeys(raw, ['productId', 'id', 'insumoId', 'name', 'productName', 'qty', 'quantity', 'qtyPerUnit', 'unit'], p);
      const ids = [...new Set(['productId', 'id', 'insumoId'].map((key) => entry[key]).filter((id) => id != null && id !== ''))];
      if (ids.length !== 1) fail(`${p}: referencia ausente o aliases contradictorios`);
      const quantities = ['qty', 'quantity', 'qtyPerUnit'].filter((key) => entry[key] !== undefined).map((key) => numeric(entry[key]));
      if (new Set(quantities).size > 1) fail(`${p}: cantidades contradictorias`);
      const id = reference(ids[0], p);
      const target = catalog.get(id);
      const unit = normalizeUnit(entry.unit || target?.unit);
      if (target && unit !== normalizeUnit(target.unit)) fail(`${p}: unidad distinta al producto referenciado`);
      return { productRef: id, name: typeof entry.name === 'string' ? entry.name : (target?.name || ''), qty: quantities[0] ?? 1, unit };
    });
  };
  LEGACY_FIELDS.forEach((key) => { if (hasContent(product[key]) || (key === 'recipeComponents' && Array.isArray(product[key]))) fail(`${key}: estructura histórica no verificada; requiere revisión antes de exportar`); });
  const modernCategory = normalizeCategoryValue(product.category);
  const hasLegacyCategory = Object.prototype.hasOwnProperty.call(product, 'categoria');
  const legacyCategory = normalizeCategoryValue(product.categoria);
  if (hasLegacyCategory && product.categoria != null && typeof product.categoria !== 'string') fail('categoria: alias histórico inválido; requiere revisión antes de exportar');
  if (modernCategory && legacyCategory && normalizeText(modernCategory) !== normalizeText(legacyCategory)) fail('category/categoria: valores contradictorios; requiere revisión antes de exportar');
  const canonicalCategory = modernCategory || legacyCategory;

  const legacyMode = String(product.mode ?? '').trim().toLowerCase();
  if (['icecream', 'helado', 'ice', 'choice', 'choices', 'choicegroups', 'backflush'].some((token) => legacyMode.includes(token))) fail('mode: configuración legacy funcional; requiere revisión antes de exportar');
  LEGACY_ICE_CREAM_FIELDS.forEach((key) => {
    if (hasFunctionalLegacyIceCreamValue(product[key])) fail(`${key}: configuración legacy de helado; requiere revisión antes de exportar`);
  });
  LEGACY_ICE_CREAM_FLAG_FIELDS.forEach((key) => {
    if (truthyLegacyFlag(product[key])) fail(`${key}: configuración legacy de helado; requiere revisión antes de exportar`);
  });
  if (product._promotionResolved) fail('Promoción temporal expandida: selecciona el producto original del catálogo');
  let config = null;
  if (product.config != null) {
    const source = checkKeys(product.config, CONFIG_FIELDS, 'config');
    config = {};
    if (source.isIceCream !== undefined) config.isIceCream = source.isIceCream;
    if (source.recipe != null) {
      const recipe = checkKeys(source.recipe, ['baseItems', 'choiceGroups'], 'config.recipe');
      if (recipe.choiceGroups !== undefined && !Array.isArray(recipe.choiceGroups)) fail('choiceGroups: debe ser una lista');
      config.recipe = {
        baseItems: items(recipe.baseItems, 'recipe.baseItems'),
        choiceGroups: (Array.isArray(recipe.choiceGroups) ? recipe.choiceGroups : []).map((raw, index) => {
          const group = checkKeys(raw, ['id', 'label', 'min', 'max', 'perPickQty', 'unit', 'fromCategory', 'defaultProductId'], `choiceGroups[${index}]`);
          const category = typeof group.fromCategory === 'string' ? group.fromCategory.trim() : '';
          const unit = normalizeUnit(group.unit);
          const members = [...catalog.values()].filter((p) => p.deleted !== true && String(p.type || '').toLowerCase() === 'insumo' && normalizeText(typeof p.category === 'string' ? p.category : p.category?.value) === normalizeText(category) && normalizeUnit(p.unit) === unit);
          members.forEach((p) => reference(p.id, `choiceGroups[${index}]`));
          const selector = { kind: 'category', sourceCategory: category, unit, memberRefs: members.map((p) => p.id).sort() };
          selectors.push(selector);
          const result = { id: group.id ?? `choice-${index + 1}`, label: group.label, min: numeric(group.min, 0), max: numeric(group.max, 0), perPickQty: numeric(group.perPickQty, 1), unit, selectorRef: selectors.length - 1 };
          if (group.defaultProductId !== undefined && group.defaultProductId !== '') result.defaultProductRef = reference(group.defaultProductId, 'defaultProductId');
          if (!members.length && result.min === 0) warnings.push({ code: 'EMPTY_OPTIONAL_CATEGORY', message: `La categoría opcional «${category}» no tiene insumos elegibles.` });
          return result;
        }),
      };
    }
    if (source.baseInsumoId !== undefined && source.baseInsumoId !== '') config.baseInsumoRef = reference(source.baseInsumoId, 'baseInsumoId');
    ['base', 'baseInsumoName'].forEach((key) => { if (source[key] !== undefined) config[key] = source[key]; });
    ['sabores', 'gramosPorSabor', 'servilletas', 'cucharas'].forEach((key) => { if (source[key] !== undefined) config[key] = numeric(source[key]); });
    if (source.extraItems !== undefined) config.extraItems = items(source.extraItems, 'extraItems');
    if (source.isIceCream === true) warnings.push({ code: 'IMPLICIT_ICE_CREAM', message: 'Este helado utiliza dependencias implícitas del POS (sabores por nombre, servilletas/cucharas y posible base por nombre). Deben revisarse durante la importación; no se garantiza que estén incluidas.' });
    if (source.promotion != null) {
      const p = checkKeys(source.promotion, ['enabled', 'components', 'sourceProductId', 'sourceProductName', 'quantity', 'weekdays'], 'promotion');
      if (source.recipe || source.isIceCream === true) fail('Promoción con receta expandida o configuración ambigua; exporta la promoción original');
      // Validate the original array BEFORE any normalization: never filter out a
      // malformed component or fall back to legacy fields to make it exportable.
      let component = p;
      if (Object.prototype.hasOwnProperty.call(p, 'components')) {
        if (!Array.isArray(p.components) || p.components.length === 0) {
          fail('promotion.components: debe contener un componente válido');
          component = {};
        } else if (p.components.length > 1) {
          fail('Esta promoción contiene varios productos base y todavía no es compatible con el formato de transferencia entre estas instalaciones.');
          component = {};
        } else {
          component = checkKeys(p.components[0], ['sourceProductId', 'sourceProductName', 'quantity'], 'promotion.components[0]');
          ['sourceProductId', 'sourceProductName', 'quantity'].forEach((key) => {
            if (!Object.prototype.hasOwnProperty.call(p, key)) return;
            const agrees = key === 'quantity'
              ? numeric(p[key]) === numeric(component[key])
              : p[key] === component[key];
            if (!agrees) fail(`promotion.components contradice ${key} antiguo`);
          });
        }
      }
      const quantity = numeric(component.quantity);
      if (!Number.isInteger(quantity) || quantity < 2) fail('La promoción requiere una cantidad entera de al menos 2 para ser compatible con schemaVersion 1.');
      config.promotion = {
        enabled: p.enabled,
        sourceProductRef: reference(component.sourceProductId, 'promotion.sourceProductId'),
        sourceProductName: component.sourceProductName ?? '',
        quantity,
        weekdays: Array.isArray(p.weekdays) ? p.weekdays.map((day) => numeric(day)) : p.weekdays,
      };
    }
  }
  const data = {
    name: product.name, barcode: product.barcode == null ? '' : String(product.barcode), price: numeric(product.price, 0), costPrice: numeric(product.costPrice, 0),
    category: canonicalCategory,
    type: product.type ?? 'final', unit: product.type === 'insumo' ? normalizeUnit(product.unit) : (product.unit || ''),
    config, excludeFromEntries: product.excludeFromEntries ?? false,
  };
  if (product.code !== undefined && product.code !== null) data.code = String(product.code);
  if (product.sku !== undefined && product.sku !== null) data.sku = String(product.sku);
  if (product.trackStock !== undefined) data.trackStock = product.trackStock;
  if (product.recipeMode !== undefined) data.recipeMode = product.recipeMode;
  if (product.yieldConfig != null) {
    const y = checkKeys(product.yieldConfig, ['enabled', 'expectedYieldPercent'], 'yieldConfig');
    data.yieldConfig = { enabled: y.enabled, expectedYieldPercent: numeric(y.expectedYieldPercent, 90) };
  } else if (data.type === 'insumo' && data.unit === 'g') {
    data.yieldConfig = { enabled: isYieldTrackingProduct(product), expectedYieldPercent: 90 };
    if (data.yieldConfig.enabled) warnings.push({ code: 'IMPLICIT_YIELD', message: 'Se representa explícitamente el rendimiento que esta instalación activa por el nombre «mezcla».' });
  }
  return { data, references: [...references], selectors, warnings, errors, status: errors.length ? 'BLOCKED' : warnings.length ? 'WARNING' : 'SUPPORTED' };
}
